import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
    LayoutDashboard, PieChart, Star, Search, 
    User, LogOut, Menu, X, Swords, Bell, 
    ArrowRightLeft, Target, ChevronDown, Activity, Shield, Globe, Eye, Calendar,
    AlertTriangle, Coins, TrendingUp, Settings, RefreshCw, CheckCircle, LayoutGrid, Crown
} from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { api } from '../services/apiClient';
import { useTranslation } from 'react-i18next'; 

const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    
    const [showNotif, setShowNotif] = useState(false);
    const { notifications, unreadCount, markAsRead, fetchNotifications } = useNotifications();
    const notifRef = useRef(null);

    const location = useLocation();
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language?.substring(0, 2).toUpperCase() || 'FR';

    const searchParams = new URLSearchParams(location.search);
    const currentHeatmapIndex = searchParams.get('index') || localStorage.getItem('keyforin_heatmap_index') || 'SP500';

    const toggleLanguage = () => {
        const nextLang = currentLanguage === 'FR' ? 'en' : 'fr';
        i18n.changeLanguage(nextLang);
    };

    useEffect(() => {
        if (token) {
            api.get('/auth/me').then(res => {
                setIsAdmin(res.data.is_admin);
            }).catch(err => console.error(err));
        }
    }, [token, location.pathname]);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notifRef.current && !notifRef.current.contains(event.target)) setShowNotif(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        setIsAdmin(false);
        navigate('/login');
    };

    const mainLinks = [
        { path: '/dashboard', label: t('header.dashboard'), icon: LayoutDashboard },
        { path: '/portfolio', label: t('header.portfolio'), icon: PieChart },
        { path: '/watchlist', label: t('header.watchlist'), icon: Star },
    ];

    const toolLinks = [
        { path: '/screener', label: t('header.screener'), icon: Search, desc: t('header.screener_desc') },
        { path: '/compare', label: t('header.compare'), icon: ArrowRightLeft, desc: t('header.compare_desc') },
        { path: '/valuation', label: t('header.fair_value'), icon: Target, desc: t('header.fair_value_desc') },
        { path: '/arena', label: t('header.arena'), icon: Swords, desc: t('header.arena_desc') },
        { path: '/insiders', label: t('header.insiders'), icon: Eye, desc: t('header.insiders_desc') },
        { path: '/calendar', label: t('header.calendar'), icon: Calendar, desc: t('header.calendar_desc') }
    ];

    const heatmapLinks = [
        { indexCode: 'SP500', label: 'S&P 500', desc: 'États-Unis', flag: '🇺🇸' },
        { indexCode: 'NASDAQ100', label: 'Nasdaq 100', desc: 'Tech US', flag: '🇺🇸' },
        { indexCode: 'CAC40', label: 'CAC 40', desc: 'France', flag: '🇫🇷' },
        { indexCode: 'DAX', label: 'DAX 40', desc: 'Allemagne', flag: '🇩🇪' },
        { indexCode: 'FTSE100', label: 'FTSE 100', desc: 'Royaume-Uni', flag: '🇬🇧' },
        { indexCode: 'TSX60', label: 'S&P/TSX 60', desc: 'Canada', flag: '🇨🇦' },
        { indexCode: 'ASX200', label: 'ASX 200', desc: 'Australie', flag: '🇦🇺' }
    ];

    const getNotifStyle = (type) => {
        const t = type?.toUpperCase() || '';
        if (t.includes('SELL') || t.includes('DANGER') || t.includes('DROP')) 
            return { bg: 'bg-rose-100', text: 'text-rose-600', icon: AlertTriangle };
        if (t.includes('DIVIDEND') || t.includes('CASH')) 
            return { bg: 'bg-emerald-100', text: 'text-emerald-600', icon: Coins };
        if (t.includes('BUY') || t.includes('TECH') || t.includes('OPPORTUNITY')) 
            return { bg: 'bg-indigo-100', text: 'text-indigo-600', icon: TrendingUp };
        return { bg: 'bg-slate-100', text: 'text-slate-600', icon: Bell };
    };

    return (
        <nav className={`fixed w-full z-50 transition-all duration-300 border-b ${
            isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-sm border-slate-200/50 py-2' : 'bg-white border-transparent py-3'
        }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-14">
                    
                    <div className="flex items-center gap-4 lg:gap-6">
                        <Link to="/" className="flex items-center shrink-0 mr-2">
                            <img src="/logo.gif" alt="KeyForIn" className="h-9 w-auto object-contain transition-transform hover:scale-105" />
                        </Link>

                        <div className="hidden lg:flex items-center gap-1.5">
                            {token && (
                                <>
                                    {mainLinks.map((link) => {
                                        const Icon = link.icon;
                                        const isActive = location.pathname === link.path;
                                        return (
                                            <Link key={link.path} to={link.path} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-200 ${isActive ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}>
                                                <Icon size={16} strokeWidth={isActive ? 2.5 : 2} />
                                                <span>{link.label}</span>
                                            </Link>
                                        );
                                    })}

                                    <div className="relative group ml-1">
                                        <button className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-200 ${toolLinks.some(t => t.path === location.pathname) ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}>
                                            <Activity size={16} />
                                            <span>{t('header.tools')}</span>
                                            <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
                                        </button>
                                        
                                        <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                                            <div className="p-2 flex flex-col gap-1">
                                                {toolLinks.map((tool) => {
                                                    const isActive = location.pathname === tool.path;
                                                    return (
                                                        <Link key={tool.path} to={tool.path} className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${isActive ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                                                            <div className={`mt-0.5 p-2 rounded-lg ${isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-white border border-slate-200 text-slate-600 shadow-sm'}`}>
                                                                <tool.icon size={16} />
                                                            </div>
                                                            <div>
                                                                <div className={`text-sm font-bold ${isActive ? 'text-indigo-900' : 'text-slate-800'}`}>{tool.label}</div>
                                                                <div className="text-xs font-medium text-slate-500 mt-0.5">{tool.desc}</div>
                                                            </div>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="relative group ml-1">
                                        <button className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-200 ${location.pathname === '/heatmap' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}>
                                            <LayoutGrid size={16} />
                                            <span>Heatmaps</span>
                                            <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
                                        </button>
                                        
                                        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                                            <div className="p-2 flex flex-col gap-1">
                                                {heatmapLinks.map((hm) => {
                                                    const isActive = location.pathname === '/heatmap' && currentHeatmapIndex === hm.indexCode;
                                                    return (
                                                        <Link key={hm.indexCode} to={`/heatmap?index=${hm.indexCode}`} className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${isActive ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                                                            <div className={`mt-0.5 w-10 h-10 rounded-xl flex items-center justify-center text-xl ${isActive ? 'bg-indigo-100' : 'bg-slate-50 border border-slate-100 shadow-sm'}`}>
                                                                {hm.flag}
                                                            </div>
                                                            <div>
                                                                <div className={`text-sm font-bold ${isActive ? 'text-indigo-900' : 'text-slate-800'}`}>{hm.label}</div>
                                                                <div className="text-xs font-medium text-slate-500 mt-0.5">{hm.desc}</div>
                                                            </div>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="hidden lg:flex items-center gap-3 shrink-0">
                        <button onClick={toggleLanguage} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors">
                            <Globe size={14} /> {currentLanguage}
                        </button>
                        
                        <div className="w-px h-5 bg-slate-200 mx-1"></div>

                        {token ? (
                            <>
                                <Link to="/pricing" className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-xs font-black uppercase tracking-widest shadow-md hover:shadow-lg hover:scale-105 transition-all">
                                    <Crown size={14} /> PRO
                                </Link>

                                <div className="relative" ref={notifRef}>
                                    <button onClick={() => setShowNotif(!showNotif)} className={`p-2 rounded-xl transition-all duration-200 relative ${showNotif ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}>
                                        <Bell size={20} />
                                        {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white animate-pulse"></span>}
                                    </button>

                                    {showNotif && (
                                        <div className="absolute right-0 mt-3 w-[360px] bg-white border border-slate-100 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
                                            <div className="px-4 py-3 border-b border-slate-50 flex justify-between items-center bg-slate-50/80 backdrop-blur-sm">
                                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                                    {t('header.notifications')}
                                                    {unreadCount > 0 && <span className="bg-indigo-600 text-white text-[10px] px-2 py-0.5 rounded-full">{unreadCount}</span>}
                                                </h3>
                                                <div className="flex items-center gap-3">
                                                    <button onClick={fetchNotifications} className="text-slate-400 hover:text-indigo-600 transition-colors" title="Rafraîchir">
                                                        <RefreshCw size={14} />
                                                    </button>
                                                    <Link to="/profile" onClick={() => setShowNotif(false)} className="text-slate-400 hover:text-slate-900 transition-colors" title="Paramètres">
                                                        <Settings size={16} />
                                                    </Link>
                                                </div>
                                            </div>
                                            <div className="max-h-[400px] overflow-y-auto no-scrollbar">
                                                {notifications.length === 0 ? (
                                                    <div className="p-8 flex flex-col items-center justify-center text-center">
                                                        <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-3 text-slate-300">
                                                            <CheckCircle size={24} />
                                                        </div>
                                                        <p className="text-slate-500 text-sm font-bold">{t('header.no_notifs', 'Vous êtes à jour !')}</p>
                                                    </div>
                                                ) : (
                                                    <div className="divide-y divide-slate-50">
                                                        {notifications.map(n => {
                                                            const style = getNotifStyle(n.type);
                                                            const Icon = style.icon;
                                                            return (
                                                                <div key={n.id} onClick={() => markAsRead(n.id)} className={`relative px-4 py-4 hover:bg-slate-50 cursor-pointer transition-all duration-200 ${n.is_read ? 'opacity-60 bg-white' : 'bg-slate-50/50'}`}>
                                                                    {!n.is_read && <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 rounded-r-full"></div>}
                                                                    <div className="flex gap-4">
                                                                        <div className={`mt-0.5 w-10 h-10 rounded-xl shrink-0 flex items-center justify-center ${style.bg} ${style.text}`}>
                                                                            <Icon size={20} />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className={`text-sm leading-snug ${n.is_read ? 'text-slate-600' : 'text-slate-900 font-bold'}`}>{n.message}</p>
                                                                            <div className="flex items-center justify-between mt-2">
                                                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{n.symbol || 'SYSTEM'}</span>
                                                                                <span className="text-[10px] font-medium text-slate-400">
                                                                                    {new Date(n.created_at).toLocaleDateString()}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                            {notifications.length > 0 && (
                                                <div className="border-t border-slate-100 p-2 bg-slate-50">
                                                    <button className="w-full text-center text-xs font-bold text-slate-500 hover:text-slate-900 py-2">
                                                        {t('header.mark_all_read', 'Tout marquer comme lu')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="relative group pl-3 border-l border-slate-200">
                                    <button className="flex items-center gap-2 p-1.5 pr-3 text-sm font-bold text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors">
                                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 border border-slate-300">
                                            <User size={16} />
                                        </div>
                                        <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300 text-slate-400" />
                                    </button>

                                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                                        <div className="p-2 flex flex-col gap-1">
                                            <Link to="/profile" className="flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-slate-50 text-slate-700 font-bold text-sm">
                                                <div className="p-1.5 bg-slate-100 rounded-lg text-slate-500"><User size={16} /></div>
                                                {t('header.my_account')}
                                            </Link>
                                            {isAdmin && (
                                                <Link to="/admin" className="flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-indigo-50 text-indigo-700 font-bold text-sm">
                                                    <div className="p-1.5 bg-indigo-100 rounded-lg text-indigo-600"><Shield size={16} /></div>
                                                    {t('header.admin_area')}
                                                </Link>
                                            )}
                                            <div className="h-px bg-slate-100 my-1"></div>
                                            <button onClick={handleLogout} className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-rose-50 text-rose-600 font-bold text-sm text-left">
                                                <div className="p-1.5 bg-rose-100 rounded-lg text-rose-500"><LogOut size={16} /></div>
                                                {t('header.logout')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Link to="/login" className="text-slate-600 font-bold text-sm hover:text-slate-900 transition-colors">{t('header.login')}</Link>
                                <Link to="/register" className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800 shadow-md">{t('header.register')}</Link>
                            </div>
                        )}
                    </div>

                    <div className="lg:hidden flex items-center gap-4">
                        <button onClick={toggleLanguage} className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 text-slate-600 text-[10px] font-bold">
                            <Globe size={12} /> {currentLanguage}
                        </button>
                        <button onClick={() => setIsOpen(!isOpen)} className="text-slate-600 hover:text-slate-900 p-2 bg-slate-50 rounded-xl">
                            {isOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {isOpen && (
                <div className="lg:hidden bg-white border-t border-slate-100 absolute w-full shadow-2xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-top-2">
                    <div className="px-4 pt-4 pb-8 space-y-4">
                        {token ? (
                            <>
                                <div className="px-2 mb-4">
                                    <Link to="/pricing" onClick={() => setIsOpen(false)} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 text-white font-black shadow-md">
                                        <Crown size={18} /> Passer à l'offre PRO
                                    </Link>
                                </div>

                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest px-4 mb-2 mt-2">{t('header.member_area')}</div>
                                <div className="space-y-1 border-t border-slate-100 pt-2">
                                    {mainLinks.map((link) => (
                                        <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-bold ${location.pathname === link.path ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                                            <link.icon size={20} /> {link.label}
                                        </Link>
                                    ))}
                                </div>
                                <div className="pt-2">
                                    <div className="text-xs font-bold text-slate-400 uppercase tracking-widest px-4 mb-2">{t('header.tools')}</div>
                                    <div className="space-y-1 bg-slate-50 p-2 rounded-2xl mx-2">
                                        {toolLinks.map((tool) => (
                                            <Link key={tool.path} to={tool.path} onClick={() => setIsOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${location.pathname === tool.path ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:bg-white hover:shadow-sm'}`}>
                                                <tool.icon size={18} /> {tool.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                                <div className="pt-2">
                                    <div className="text-xs font-bold text-slate-400 uppercase tracking-widest px-4 mb-2">Heatmaps Mondiales</div>
                                    <div className="space-y-1 bg-slate-50 p-2 rounded-2xl mx-2">
                                        {heatmapLinks.map((hm) => (
                                            <Link key={hm.indexCode} to={`/heatmap?index=${hm.indexCode}`} onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-slate-600 hover:bg-white hover:shadow-sm">
                                                <span className="text-lg">{hm.flag}</span> {hm.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                                <div className="border-t border-slate-100 pt-4 space-y-2">
                                    {isAdmin && (
                                        <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-indigo-600 bg-indigo-50 rounded-xl font-bold" onClick={() => setIsOpen(false)}>
                                            <Shield size={20} /> {t('header.admin_area')}
                                        </Link>
                                    )}
                                    <Link to="/profile" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-xl font-bold" onClick={() => setIsOpen(false)}>
                                        <User size={20} /> {t('header.my_account')}
                                    </Link>
                                    <button onClick={() => { handleLogout(); setIsOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-rose-600 hover:bg-rose-50 rounded-xl text-left font-bold">
                                        <LogOut size={20} /> {t('header.logout')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col gap-3 mt-2 pt-2">
                                <Link to="/login" className="w-full text-center py-3 rounded-xl border border-slate-200 font-bold text-slate-700" onClick={() => setIsOpen(false)}>{t('header.login')}</Link>
                                <Link to="/register" className="w-full text-center py-3 rounded-xl bg-slate-900 text-white font-bold shadow-lg" onClick={() => setIsOpen(false)}>{t('header.register')}</Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Header;
